import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, FileText, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { getLoanReportBlob, downloadLoanReport } from '../../utils/pdfExport';
import { formatCurrency, getStatusColor } from '../../utils/helper';

const StatusBadge = ({ status }) => {
    const normalizedStatus = status === 'compliant' ? 'ok' : status === 'at_risk' ? 'warning' : status === 'breach' ? 'critical' : status;
    const colors = getStatusColor(normalizedStatus);
    const icons = { ok: CheckCircle, warning: AlertTriangle, critical: XCircle, active: CheckCircle, archived: CheckCircle };
    const labels = { ok: 'Compliant', warning: 'At Risk', critical: 'Breach', active: 'Active', archived: 'Archived' };
    const Icon = icons[normalizedStatus] || CheckCircle;

    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
      <Icon size={12} />
            {labels[normalizedStatus] || status}
    </span>
    );
};


const PDFPreviewHTML = ({ loan, alerts = [], breachHistory = [] }) => {
    const statusColors = {
        ok: 'bg-emerald-500',
        warning: 'bg-amber-500',
        critical: 'bg-red-500'
    };
    const statusLabels = { ok: 'Compliant', warning: 'At Risk', critical: 'Breach' };

    return (
        <div className="bg-white border border-slate-300 shadow-lg mx-auto max-w-2xl">
            {}
            <div className="bg-blue-600 text-white p-6">
                <h1 className="text-xl font-bold">COVENANT TRACKER</h1>
                <p className="text-blue-200 text-sm mt-1">Loan Compliance Report</p>
                <p className="text-xs text-blue-200 mt-2">Generated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>

            {}
            <div className="p-5 border-b border-slate-200">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-sm font-bold text-blue-600 uppercase">Loan Summary</h2>
                    <span className={`${statusColors[loan.status]} text-white text-xs px-3 py-1 rounded font-medium`}>
                        {statusLabels[loan.status]}
                    </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Borrower:</span> <strong>{loan.borrower}</strong></div>
                    <div><span className="text-slate-500">Amount:</span> <strong>{formatCurrency(loan.amount)}</strong></div>
                    <div><span className="text-slate-500">Lead Arranger:</span> <strong>{loan.leadArranger}</strong></div>
                    <div><span className="text-slate-500">Sector:</span> <strong>{loan.sector}</strong></div>
                    <div><span className="text-slate-500">Country:</span> <strong>{loan.country}</strong></div>
                    <div><span className="text-slate-500">Maturity:</span> <strong>{new Date(loan.maturityDate).toLocaleDateString('en-GB')}</strong></div>
                </div>
            </div>

            {}
            <div className="p-5 border-b border-slate-200">
                <h2 className="text-sm font-bold text-blue-600 uppercase mb-3">Covenant Status</h2>
                <p className="text-xs text-slate-500 mb-3">
                    {loan.covenants.length} Total | {loan.covenants.filter(c => c.status === 'ok').length} Compliant | {loan.covenants.filter(c => c.status === 'warning').length} At Risk | {loan.covenants.filter(c => c.status === 'critical').length} Breach
                </p>
                <table className="w-full text-xs">
                    <thead className="bg-slate-800 text-white">
                        <tr>
                            <th className="text-left p-2">Covenant</th>
                            <th className="text-left p-2">Threshold</th>
                            <th className="text-left p-2">Current</th>
                            <th className="text-left p-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loan.covenants.map((c, i) => (
                            <tr key={i} className="border-b border-slate-100">
                                <td className="p-2">{c.name}</td>
                                <td className="p-2">{c.operator} {c.threshold}</td>
                                <td className="p-2 font-semibold">{c.current}</td>
                                <td className={`p-2 font-semibold ${c.status === 'ok' ? 'text-emerald-600' : c.status === 'warning' ? 'text-amber-600' : 'text-red-600'}`}>
                                    {statusLabels[c.status]}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {}
            {alerts.length > 0 && (
                <div className="p-5 border-b border-slate-200">
                    <h2 className="text-sm font-bold text-blue-600 uppercase mb-3">Active Alerts</h2>
                    <table className="w-full text-xs">
                        <thead className="bg-slate-800 text-white">
                            <tr>
                                <th className="text-left p-2">Severity</th>
                                <th className="text-left p-2">Title</th>
                                <th className="text-left p-2">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {alerts.map((a, i) => (
                                <tr key={i} className="border-b border-slate-100">
                                    <td className={`p-2 font-bold ${a.type === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>
                                        {a.type.toUpperCase()}
                                    </td>
                                    <td className="p-2">{a.title}</td>
                                    <td className="p-2">{new Date(a.createdAt).toLocaleDateString('en-GB')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {}
            <div className="p-4 border-t border-slate-200 flex justify-between text-xs text-slate-400">
                <span>Covenant Tracker - LMA Edge Hackathon 2025</span>
                <span>Page 1 of 1</span>
            </div>
        </div>
    );
};

function ReportPreviewModal({ isOpen, onClose, loan, alerts = [], breachHistory = [] }) {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('preview');
    const [error, setError] = useState(null);

    useEffect(() => {
        let url = null;

        if (isOpen && loan) {
            setIsLoading(true);
            setError(null);
            setPdfUrl(null);


            const timer = setTimeout(() => {
                try {
                    const blob = getLoanReportBlob(loan, alerts, breachHistory);
                    url = URL.createObjectURL(blob);
                    setPdfUrl(url);
                } catch (err) {
                    console.error('Error generating PDF:', err);
                    setError(err.message || 'Failed to generate PDF');
                }
                setIsLoading(false);
            }, 100);

            return () => {
                clearTimeout(timer);
                if (url) {
                    URL.revokeObjectURL(url);
                }
            };
        }
    }, [isOpen, loan, alerts, breachHistory]);

    const handleDownload = () => {
        downloadLoanReport(loan, alerts, breachHistory);
    };

    const handleElectronExport = async () => {
        if (window.electronAPI) {
            const blob = getLoanReportBlob(loan, alerts, breachHistory);
            const reader = new FileReader();
            reader.onload = async () => {
                const base64 = reader.result.split(',')[1];
                const fileName = `Covenant_Report_${loan.borrower.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                await window.electronAPI.exportPDF({ fileName, content: base64 });
            };
            reader.readAsDataURL(blob);
        } else {
            handleDownload();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={onClose}
            />

            {}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">

                    {}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <FileText size={20} className="text-blue-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-slate-800">
                                    Compliance Report
                                </h2>
                                <p className="text-sm text-slate-500">
                                    {loan?.borrower} • {new Date().toLocaleDateString('en-GB')}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleElectronExport}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Download size={18} />
                                Download PDF
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <X size={20} className="text-slate-500" />
                            </button>
                        </div>
                    </div>

                    {}
                    <div className="flex border-b border-slate-200 px-6">
                        <button
                            onClick={() => setActiveTab('preview')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'preview'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            PDF Preview
                        </button>
                        <button
                            onClick={() => setActiveTab('data')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'data'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            Report Data
                        </button>
                    </div>

                    {}
                    <div className="overflow-auto" style={{ height: 'calc(90vh - 140px)' }}>
                        {activeTab === 'preview' && (
                            <div className="p-6">
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <Loader2 size={40} className="text-blue-600 animate-spin mb-4" />
                                        <p className="text-slate-500">Generating report...</p>
                                    </div>
                                ) : pdfUrl ? (
                                    <iframe
                                        src={pdfUrl}
                                        className="w-full h-[600px] border border-slate-200 rounded-lg"
                                        title="PDF Preview"
                                    />
                                ) : loan ? (
                                    <div className="bg-slate-100 p-6 rounded-lg">
                                        <p className="text-center text-sm text-slate-500 mb-4">
                                            HTML Preview (click "Download PDF" for the actual PDF file)
                                        </p>
                                        <PDFPreviewHTML loan={loan} alerts={alerts} breachHistory={breachHistory} />
                                    </div>
                                ) : (
                                    <div className="text-center py-10 text-slate-500">
                                        No loan data available
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'data' && loan && (
                            <div className="p-6 space-y-6">
                                {}
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-800 mb-3 uppercase tracking-wide">
                                        Loan Summary
                                    </h3>
                                    <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-4 gap-4">
                                        <div>
                                            <p className="text-xs text-slate-500">Borrower</p>
                                            <p className="font-medium text-slate-800">{loan.borrower}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">Amount</p>
                                            <p className="font-medium text-slate-800">{formatCurrency(loan.amount)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">Sector</p>
                                            <p className="font-medium text-slate-800">{loan.sector}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500">Status</p>
                                            <StatusBadge status={loan.status} />
                                        </div>
                                    </div>
                                </div>

                                {}
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-800 mb-3 uppercase tracking-wide">
                                        Covenant Status
                                    </h3>
                                    <div className="overflow-hidden border border-slate-200 rounded-xl">
                                        <table className="w-full">
                                            <thead className="bg-slate-50">
                                            <tr>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Covenant</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Threshold</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Current</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Headroom</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                            {loan.covenants.map((covenant, idx) => {
                                                const headroom = covenant.operator === '<='
                                                    ? ((covenant.threshold - covenant.current) / covenant.threshold * 100).toFixed(1)
                                                    : ((covenant.current - covenant.threshold) / covenant.threshold * 100).toFixed(1);

                                                return (
                                                    <tr key={idx} className="hover:bg-slate-50">
                                                        <td className="px-4 py-3 font-medium text-slate-800">{covenant.name}</td>
                                                        <td className="px-4 py-3 text-slate-600">{covenant.operator} {covenant.threshold}</td>
                                                        <td className="px-4 py-3 text-slate-800 font-semibold">{covenant.current}</td>
                                                        <td className={`px-4 py-3 font-medium ${
                                                            parseFloat(headroom) < 0 ? 'text-red-600' :
                                                                parseFloat(headroom) < 10 ? 'text-amber-600' : 'text-emerald-600'
                                                        }`}>
                                                            {headroom}%
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <StatusBadge status={covenant.status} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {}
                                {alerts.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-800 mb-3 uppercase tracking-wide">
                                            Active Alerts ({alerts.length})
                                        </h3>
                                        <div className="space-y-2">
                                            {alerts.map((alert, idx) => (
                                                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                                                    {alert.type === 'critical' ? (
                                                        <XCircle size={18} className="text-red-500 mt-0.5" />
                                                    ) : (
                                                        <AlertTriangle size={18} className="text-amber-500 mt-0.5" />
                                                    )}
                                                    <div>
                                                        <p className="font-medium text-slate-800">{alert.title}</p>
                                                        <p className="text-sm text-slate-500">{alert.message}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {}
                                {breachHistory.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-800 mb-3 uppercase tracking-wide">
                                            Breach History ({breachHistory.length})
                                        </h3>
                                        <div className="overflow-hidden border border-slate-200 rounded-xl">
                                            <table className="w-full">
                                                <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Date</th>
                                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Covenant</th>
                                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Value</th>
                                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Threshold</th>
                                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
                                                </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                {breachHistory.map((breach, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-4 py-3 text-slate-600">
                                                            {new Date(breach.date).toLocaleDateString('en-GB')}
                                                        </td>
                                                        <td className="px-4 py-3 font-medium text-slate-800">{breach.covenant}</td>
                                                        <td className="px-4 py-3 text-red-600 font-medium">{breach.value}</td>
                                                        <td className="px-4 py-3 text-slate-600">{breach.threshold}</td>
                                                        <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                    breach.status === 'resolved'
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-red-100 text-red-700'
                                }`}>
                                  {breach.status === 'resolved' ? 'Resolved' : 'Active'}
                                </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportPreviewModal;